import { Modal, Stack } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import { useUploadEfxTransactionACAM220fileMutation } from '@/api/v1';
import EyeIcon from '@/assets/icons/eye-open.svg?react';
import { DocumentPreviewWidget } from '@/components/molecules/DocumentPreviewWidget';
import { FileInput } from '@/components/molecules/FileInput';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import { TransactionDocument } from '@/types/transaction';

interface Props {
  readonly transactionId: string;
  readonly documents?: readonly TransactionDocument[];
  readonly isEfx?: boolean;
}

export const TransactionDocumentsList: React.FC<Props> = (props: Props): React.ReactElement => {
  const { transactionId, documents = [], isEfx = false } = props;
  const tr = useTranslator();
  const toast = useToast();
  const [uploadEfxTransactionACAM220file, { status, error }] =
    useUploadEfxTransactionACAM220fileMutation();
  const [previewUrl, setPreviewUrl] = React.useState<string | undefined>();
  const [attachment, setAttachment] = React.useState<File | null>(null);

  const onPreview = React.useCallback((viewUrl?: string): VoidFunction => {
    return (): void => {
      setPreviewUrl(viewUrl);
    };
  }, []);

  const closePreview = React.useCallback((): void => {
    setPreviewUrl(undefined);
  }, []);

  if (documents.length === 0 && !isEfx) {
    return <></>;
  }

  const handleChangeFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const target = event.target;
    const files = target.files ?? [];

    if (files[0]) {
      setAttachment(files[0]);
      try {
        await uploadEfxTransactionACAM220file({
          id: transactionId,
          file: files[0],
        }).unwrap();

        toast.show(tr('UPLOAD_SUCCESS_MESSAGE'), 'success');
      } catch (err) {
        console.error('Error uploading file:', err);
        toast.show(tr('UPLOAD_ERROR_MESSAGE'), 'error');
      }
    }
  };

  const handleRemoveFile = (): void => {
    setAttachment(null);
  };

  return (
    <Box>
      <Typography variant="h7">{tr('SUPPORTING_DOCUMENTATION')}</Typography>
      <Stack mt={3} gap={2}>
        {documents.map(
          (document: TransactionDocument): React.ReactElement => (
            <DocumentPreviewWidget
              key={document.id}
              size={document.size}
              name={document.name}
              icon={EyeIcon}
              error={null}
              status={QueryStatus.fulfilled}
              onIconClick={onPreview(document.viewUrl)}
            />
          ),
        )}
        {isEfx && documents.length === 0 && (
          <FileInput
            name="attachments"
            buttonTitle="Upload ACAM220 file"
            accept=".csv"
            value={attachment}
            status={status}
            error={error ? tr('UPLOAD_ERROR_MESSAGE') : null}
            onChange={handleChangeFile}
            onRemove={handleRemoveFile}
          />
        )}
      </Stack>
      <Modal open={!!previewUrl} onClose={closePreview}>
        <Box sx={styles.modalContent}>
          <embed src={previewUrl} width="100%" height="100%" />
        </Box>
      </Modal>
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  modalContent: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90vw',
    height: '90vh',
  },
};
